import { deepCopy, simpleDeepCopy } from './data_utils';
import { safeCallback } from './func_arr';
import { DeepCopyKind } from './global_options';
import { MutationObserverTarget } from './mutation_observer';
import { triggerWarning } from './warning';
// eslint-disable-next-line @typescript-eslint/unbound-method
const hasOwnProperty = Object.prototype.hasOwnProperty;
export var NormalizedPropertyType;
(function (NormalizedPropertyType) {
    NormalizedPropertyType["Invalid"] = "invalid";
    NormalizedPropertyType["Any"] = "any";
    NormalizedPropertyType["String"] = "string";
    NormalizedPropertyType["Number"] = "number";
    NormalizedPropertyType["Boolean"] = "boolean";
    NormalizedPropertyType["Object"] = "object";
    NormalizedPropertyType["Array"] = "array";
    NormalizedPropertyType["Function"] = "function";
})(NormalizedPropertyType || (NormalizedPropertyType = {}));
export const shallowMerge = (dest, src) => {
    const keys = Object.keys(src);
    for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        if (Object.prototype.hasOwnProperty.call(dest, key)) {
            if (key[0] === '_') {
                triggerWarning(`data field "${key}" from different behaviors is overriding or merging.`);
            }
            if (typeof dest[key] === 'object' &&
                typeof src[key] === 'object' &&
                src[key] !== null &&
                !Array.isArray(src[key])) {
                if (Array.isArray(dest[key])) {
                    dest[key] = dest[key].slice();
                }
                else {
                    const oldDest = dest[key];
                    const newDest = {};
                    const subKeys = Object.keys(oldDest);
                    for (let i = 0; i < subKeys.length; i += 1) {
                        const subKey = subKeys[i];
                        newDest[subKey] = oldDest[subKey];
                    }
                    dest[key] = newDest;
                }
                shallowMerge(dest[key], src[key]);
            }
            else {
                dest[key] = src[key];
            }
        }
        else {
            dest[key] = src[key];
        }
    }
};
export const getPropertyFallbackValue = (type) => {
    switch (type) {
        case "string" /* NormalizedPropertyType.String */:
            return '';
        case "number" /* NormalizedPropertyType.Number */:
            return 0;
        case "boolean" /* NormalizedPropertyType.Boolean */:
            return false;
        case "array" /* NormalizedPropertyType.Array */:
            return [];
        case "function" /* NormalizedPropertyType.Function */:
            return function () {
                /* empty */
            };
        default:
            return null;
    }
};
export const normalizePropertyTypeShortHand = (propDef) => {
    if (propDef === "string" /* NormalizedPropertyType.String */ || propDef === String) {
        return {
            type: "string" /* NormalizedPropertyType.String */,
            optionalTypes: null,
            defaultFn: () => '',
            observer: null,
            comparer: null,
            reflectIdPrefix: false,
        };
    }
    if (propDef === "number" /* NormalizedPropertyType.Number */ || propDef === Number) {
        return {
            type: "number" /* NormalizedPropertyType.Number */,
            optionalTypes: null,
            defaultFn: () => 0,
            observer: null,
            comparer: null,
            reflectIdPrefix: false,
        };
    }
    if (propDef === "boolean" /* NormalizedPropertyType.Boolean */ || propDef === Boolean) {
        return {
            type: "boolean" /* NormalizedPropertyType.Boolean */,
            optionalTypes: null,
            defaultFn: () => false,
            observer: null,
            comparer: null,
            reflectIdPrefix: false,
        };
    }
    if (propDef === "object" /* NormalizedPropertyType.Object */ || propDef === Object) {
        return {
            type: "object" /* NormalizedPropertyType.Object */,
            optionalTypes: null,
            defaultFn: () => null,
            observer: null,
            comparer: null,
            reflectIdPrefix: false,
        };
    }
    if (propDef === "array" /* NormalizedPropertyType.Array */ || propDef === Array) {
        return {
            type: "array" /* NormalizedPropertyType.Array */,
            optionalTypes: null,
            defaultFn: () => [],
            observer: null,
            comparer: null,
            reflectIdPrefix: false,
        };
    }
    if (propDef === "function" /* NormalizedPropertyType.Function */ || propDef === Function) {
        return {
            type: "function" /* NormalizedPropertyType.Function */,
            optionalTypes: null,
            defaultFn: () => function () {
                /* empty */
            },
            observer: null,
            comparer: null,
            reflectIdPrefix: false,
        };
    }
    if (propDef === "any" /* NormalizedPropertyType.Any */ || propDef === null || propDef === undefined) {
        return {
            type: "any" /* NormalizedPropertyType.Any */,
            optionalTypes: null,
            defaultFn: () => null,
            observer: null,
            comparer: null,
            reflectIdPrefix: false,
        };
    }
    return null;
};
export const normalizePropertyType = (t) => {
    if (t === "string" /* NormalizedPropertyType.String */ || t === String) {
        return "string" /* NormalizedPropertyType.String */;
    }
    if (t === "number" /* NormalizedPropertyType.Number */ || t === Number) {
        return "number" /* NormalizedPropertyType.Number */;
    }
    if (t === "boolean" /* NormalizedPropertyType.Boolean */ || t === Boolean) {
        return "boolean" /* NormalizedPropertyType.Boolean */;
    }
    if (t === "object" /* NormalizedPropertyType.Object */ || t === Object) {
        return "object" /* NormalizedPropertyType.Object */;
    }
    if (t === "array" /* NormalizedPropertyType.Array */ || t === Array) {
        return "array" /* NormalizedPropertyType.Array */;
    }
    if (t === "function" /* NormalizedPropertyType.Function */ || t === Function) {
        return "function" /* NormalizedPropertyType.Function */;
    }
    if (t === "any" /* NormalizedPropertyType.Any */ || t === null || t === undefined) {
        return "any" /* NormalizedPropertyType.Any */;
    }
    return "invalid" /* NormalizedPropertyType.Invalid */;
};
export const convertValueToType = (value, propName, prop) => {
    const type = prop.type;
    // try match optional types
    const optionalTypes = prop.optionalTypes;
    if (optionalTypes) {
        for (let i = 0; i < optionalTypes.length; i += 1) {
            if (matchTypeWithValue(optionalTypes[i], value)) {
                return value;
            }
        }
    }
    // for string
    if (type === "string" /* NormalizedPropertyType.String */) {
        if (value === null || value === undefined) {
            triggerWarning(`property "${propName}" received type-incompatible value: expected <String> but get null value. Used default value instead.`);
            return prop.defaultFn();
        }
        if (typeof value === 'object') {
            triggerWarning(`property "${propName}" received type-incompatible value: expected <String> but got object-typed value. Force converted.`);
        }
        return String(value);
    }
    // for number
    if (type === "number" /* NormalizedPropertyType.Number */) {
        // eslint-disable-next-line no-restricted-globals
        if (isFinite(value))
            return Number(value);
        if (typeof value === 'number') {
            triggerWarning(`property "${propName}" received type-incompatible value: expected <Number> but got NaN or Infinity. Used default value instead.`);
        }
        else {
            triggerWarning(`property "${propName}" received type-incompatible value: expected <Number> but got non-number value. Used default value instead.`);
        }
        return prop.defaultFn();
    }
    // for boolean
    if (type === "boolean" /* NormalizedPropertyType.Boolean */) {
        return !!value;
    }
    // for array
    if (type === "array" /* NormalizedPropertyType.Array */) {
        if (Array.isArray(value))
            return value;
        triggerWarning(`property "${propName}" received type-incompatible value: expected <Array> but got non-array value. Used default value instead.`);
        return prop.defaultFn();
    }
    // for object
    if (type === "object" /* NormalizedPropertyType.Object */) {
        if (typeof value === 'object')
            return value;
        triggerWarning(`property "${propName}" received type-incompatible value: expected <Object> but got non-object value. Used default value instead.`);
        return prop.defaultFn();
    }
    // for function
    if (type === "function" /* NormalizedPropertyType.Function */) {
        if (typeof value === 'function')
            return value;
        triggerWarning(`property "${propName}" received type-incompatible value: expected <Function> but got non-function value. Used default value instead.`);
        return prop.defaultFn();
    }
    // for any-typed, just return the value and avoid undefined
    if (value === undefined)
        return prop.defaultFn();
    return value;
};
export const matchTypeWithValue = (type, value) => {
    if (type === "string" /* NormalizedPropertyType.String */) {
        if (typeof value !== 'string')
            return false;
    }
    else if (type === "number" /* NormalizedPropertyType.Number */) {
        if (!Number.isFinite(value))
            return false;
    }
    else if (type === "boolean" /* NormalizedPropertyType.Boolean */) {
        if (typeof value !== 'boolean')
            return false;
    }
    else if (type === "object" /* NormalizedPropertyType.Object */) {
        if (typeof value !== 'object' || Array.isArray(value))
            return false;
    }
    else if (type === "array" /* NormalizedPropertyType.Array */) {
        if (typeof value !== 'object' || !Array.isArray(value))
            return false;
    }
    else if (type === "function" /* NormalizedPropertyType.Function */) {
        if (typeof value !== 'function')
            return false;
    }
    else if (value === undefined) {
        return false;
    }
    return true;
};
export var DeepCopyStrategy;
(function (DeepCopyStrategy) {
    DeepCopyStrategy[DeepCopyStrategy["None"] = 0] = "None";
    DeepCopyStrategy[DeepCopyStrategy["Simple"] = 1] = "Simple";
    DeepCopyStrategy[DeepCopyStrategy["SimpleWithRecursion"] = 2] = "SimpleWithRecursion";
})(DeepCopyStrategy || (DeepCopyStrategy = {}));
export class DataGroupObserverTree {
    constructor(propFields) {
        this.observerRoot = { sub: {} };
        this.observers = [];
        this.propFields = propFields;
    }
    cloneSub() {
        const ret = new DataGroupObserverTree(this.propFields);
        if (this.observers.length > 0) {
            ret.observerRoot = simpleDeepCopy(this.observerRoot);
            ret.observers = this.observers.slice();
        }
        return ret;
    }
    addObserver(func, dataPath) {
        const id = this.observers.length;
        this.observers.push({
            path: dataPath,
            f: func,
        });
        for (let i = 0; i < dataPath.length; i += 1) {
            const singlePath = dataPath[i];
            let cur = this.observerRoot;
            let wildcard = false;
            for (let j = 0; j < singlePath.length; j += 1) {
                const pathSlice = singlePath[j];
                if (pathSlice === '**') {
                    wildcard = true;
                    break;
                }
                if (!cur.sub[pathSlice])
                    cur.sub[pathSlice] = { sub: {} };
                cur = cur.sub[pathSlice];
            }
            if (wildcard) {
                if (!cur.wildcard)
                    cur.wildcard = [id];
                else
                    cur.wildcard.push(id);
            }
            else {
                if (!cur.listener)
                    cur.listener = [id];
                else
                    cur.listener.push(id);
            }
        }
    }
}
const callObserver = (comp, data, path, f) => {
    const args = new Array(path.length);
    for (let i = 0; i < path.length; i += 1) {
        const singlePath = path[i];
        let cur = data;
        for (let j = 0; j < singlePath.length; j += 1) {
            const slice = singlePath[j];
            if (slice === '**')
                break;
            if (typeof cur === 'object' && cur !== null) {
                cur = cur[slice];
            }
            else {
                cur = undefined;
                break;
            }
        }
        args[i] = cur;
    }
    safeCallback('Data Observer', f, comp.getMethodCaller(), args, comp || undefined);
};
const dfsMarkTriggerBitOnPath = (node, observerStatus) => {
    if (node.listener) {
        for (let i = 0; i < node.listener.length; i += 1) {
            const observerId = node.listener[i];
            observerStatus[observerId] = true;
        }
    }
    if (node.wildcard) {
        for (let i = 0; i < node.wildcard.length; i += 1) {
            const observerId = node.wildcard[i];
            observerStatus[observerId] = true;
        }
    }
    const keys = Object.keys(node.sub);
    for (let i = 0; i < keys.length; i += 1) {
        const k = keys[i];
        dfsMarkTriggerBitOnPath(node.sub[k], observerStatus);
    }
};
const markTriggerBitOnPath = (root, observerStatus, singlePath) => {
    let cur = root;
    let found = true;
    for (let i = 0; i < singlePath.length; i += 1) {
        const slice = singlePath[i];
        if (cur.wildcard) {
            const arr = cur.wildcard;
            for (let i = 0; i < arr.length; i += 1) {
                const observerId = arr[i];
                observerStatus[observerId] = true;
            }
        }
        if (!hasOwnProperty.call(cur.sub, slice)) {
            found = false;
            break;
        }
        cur = cur.sub[slice];
    }
    if (found)
        dfsMarkTriggerBitOnPath(cur, observerStatus);
};
const triggerAndCleanTriggerBit = (observers, observerStatus, comp, data) => {
    for (let i = 0; i < observers.length; i += 1) {
        const { path, f } = observers[i];
        const status = observerStatus[i];
        if (status) {
            observerStatus[i] = false;
            if (comp)
                callObserver(comp, data, path, f);
        }
    }
};
export const getDeepCopyStrategy = (level) => {
    if (level === DeepCopyKind.Simple)
        return 1 /* DeepCopyStrategy.Simple */;
    if (level === DeepCopyKind.SimpleWithRecursion)
        return 2 /* DeepCopyStrategy.SimpleWithRecursion */;
    return 0 /* DeepCopyStrategy.None */;
};
/** A data wrapper for data operations such as `setData` */
export class DataGroup {
    /* @internal */
    _$generateInnerData(data) {
        const pureDataPattern = this._$pureDataPattern;
        const dataDeepCopy = this._$dataDeepCopy;
        if (pureDataPattern || dataDeepCopy !== 0 /* DeepCopyStrategy.None */) {
            const innerData = {};
            const keys = Object.keys(data);
            for (let i = 0; i < keys.length; i += 1) {
                const k = keys[i];
                const v = data[k];
                if (pureDataPattern && pureDataPattern.test(k))
                    continue;
                if (dataDeepCopy === 0 /* DeepCopyStrategy.None */) {
                    innerData[k] = v;
                }
                else if (dataDeepCopy === 2 /* DeepCopyStrategy.SimpleWithRecursion */) {
                    innerData[k] = deepCopy(v, true);
                }
                else {
                    innerData[k] = simpleDeepCopy(v);
                }
            }
            return innerData;
        }
        return null;
    }
    constructor(associatedComponent, data, pureDataPattern, dataDeepCopy, propertyPassingDeepCopy, reflectToAttributes, observerTree, propertyComparer, unknownPropertyHandler) {
        /* @internal */
        this._$modelBindingListener = null;
        /* @internal */
        this._$pendingChanges = [];
        /* @internal */
        this._$doingUpdates = null;
        /* @internal */
        this._$recUpdateLevel = 0;
        this._$comp = associatedComponent;
        this.data = data;
        this._$pureDataPattern = pureDataPattern;
        this._$dataDeepCopy = dataDeepCopy;
        this._$propertyPassingDeepCopy = propertyPassingDeepCopy;
        this._$reflectToAttributes = reflectToAttributes && !!associatedComponent;
        this._$propFields = observerTree.propFields;
        this._$propDirty = Object.create(null);
        this._$observerTree = observerTree;
        this._$observerStatus = new Array(observerTree.observers.length);
        this._$propertyComparer = propertyComparer;
        this._$unknownPropertyHandler = unknownPropertyHandler;
        this.innerData = this._$generateInnerData(data);
    }
    /** Create a simple data group */
    static create(data) {
        return new DataGroup(null, data, null, 0 /* DeepCopyStrategy.None */, 0 /* DeepCopyStrategy.None */, false, new DataGroupObserverTree({}), null, null);
    }
    /** Replace the underlying data */
    replaceWholeData(data) {
        this.data = data;
        this.innerData = this._$generateInnerData(data);
    }
    /** Add a new common data change to queue */
    replaceDataOnPath(path, newData) {
        this._$pendingChanges.push([path, newData, undefined, undefined]);
    }
    /** Add a new array splice operation to queue */
    spliceArrayDataOnPath(path, index, del, inserts) {
        if (!Array.isArray(inserts)) {
            triggerWarning(`The splice insertion must be a string (on path "${path.join('.')}"). The change is ignored.`);
            return;
        }
        this._$pendingChanges.push([path, inserts, index !== null && index !== void 0 ? index : -1, del || 0]);
    }
    /**
     * Add a new property change to queue
     *
     * (Generally designed for template engines.)
     * If the `propName` is a property,
     * the `newData` will be deep-copied according to the `propertyPassingDeepCopy` configuration.
     * Otherwise, it returns false.
     */
    replaceProperty(propName, newData) {
        var _a, _b;
        let data = newData;
        const prop = this._$propFields[propName];
        if (!prop)
            return (_b = (_a = this._$unknownPropertyHandler) === null || _a === void 0 ? void 0 : _a.call(this._$comp, propName, newData)) !== null && _b !== void 0 ? _b : false;
        if (this._$propertyPassingDeepCopy !== 0 /* DeepCopyStrategy.None */) {
            if (this._$propertyPassingDeepCopy === 2 /* DeepCopyStrategy.SimpleWithRecursion */) {
                data = deepCopy(newData, true);
            }
            else {
                data = simpleDeepCopy(newData);
            }
        }
        this._$pendingChanges.push([[propName], data, undefined, undefined]);
        return true;
    }
    /** Discard changes in queue and generate a new queue with specified changes */
    setChanges(changes) {
        this._$pendingChanges = changes;
    }
    /** Get the data change queue */
    getChanges() {
        return this._$pendingChanges;
    }
    /**
     * Set a callback when a specified property changes
     *
     * (Generally designed for template engines.)
     */
    setModelBindingListener(propName, listener) {
        if (this._$modelBindingListener) {
            this._$modelBindingListener[propName] = listener;
        }
        else {
            const map = (this._$modelBindingListener = Object.create(null));
            map[propName] = listener;
        }
    }
    /** Apply all changes in queue */
    applyDataUpdates(skipModelListener = false) {
        var _a;
        const propFields = this._$propFields;
        const comp = this._$comp;
        const pureDataPattern = this._$pureDataPattern;
        const dataDeepCopy = this._$dataDeepCopy;
        // handling chained updates if observers are used
        const isChainedUpdates = !!this._$doingUpdates;
        let combinedChanges;
        let propChanges;
        if (this._$observerTree.observers.length > 0) {
            if (this._$doingUpdates) {
                combinedChanges = this._$doingUpdates.combined;
                propChanges = this._$doingUpdates.prop;
            }
            else {
                this._$doingUpdates = {
                    prop: [],
                    combined: [],
                    count: 0,
                };
                combinedChanges = this._$doingUpdates.combined;
                propChanges = this._$doingUpdates.prop;
            }
        }
        else {
            combinedChanges = [];
            propChanges = [];
        }
        // find all changes
        const changes = this._$pendingChanges;
        this._$pendingChanges = [];
        // apply changes and collect changing information
        for (let i = 0; i < changes.length; i += 1) {
            const change = changes[i];
            const [path, newData, maybeSpliceIndex, maybeSpliceDel] = change;
            const isSplice = maybeSpliceDel !== undefined;
            const propName = String(path[0]);
            const excluded = pureDataPattern ? pureDataPattern.test(propName) : false;
            const needInnerDataUpdates = !excluded && this.innerData;
            const prop = propFields[propName];
            let changed = true;
            // if update 1-level fields, check if it is a property
            if (prop && path.length === 1) {
                const oldData = this.data[propName];
                let normalizedSpliceIndex;
                let filteredData;
                if (isSplice) {
                    // splice update for properties
                    if (Array.isArray(oldData)) {
                        const c = change;
                        const spliceIndex = maybeSpliceIndex;
                        normalizedSpliceIndex =
                            spliceIndex >= 0 && spliceIndex < oldData.length ? spliceIndex : oldData.length;
                        c[2] = normalizedSpliceIndex;
                        oldData.splice(normalizedSpliceIndex, maybeSpliceDel, ...newData);
                    }
                    else {
                        triggerWarning(`An array splice change cannot be applied to a non-array value (on path "${path.join('.')}"). The change is ignored.`);
                    }
                    filteredData = oldData;
                }
                else {
                    // normal replace for properties
                    filteredData = convertValueToType(newData, propName, prop);
                }
                // if inner data is separated, update it
                if (needInnerDataUpdates) {
                    let innerNewData;
                    if (dataDeepCopy === 0 /* DeepCopyStrategy.None */) {
                        change[1] = innerNewData = filteredData;
                    }
                    else if (normalizedSpliceIndex !== undefined) {
                        innerNewData = this.innerData[propName];
                        let inserts;
                        if (dataDeepCopy === 2 /* DeepCopyStrategy.SimpleWithRecursion */) {
                            change[1] = inserts = deepCopy(newData, true);
                        }
                        else {
                            change[1] = inserts = simpleDeepCopy(newData);
                        }
                        ;
                        innerNewData.splice(normalizedSpliceIndex, maybeSpliceDel, ...inserts);
                    }
                    else if (dataDeepCopy === 2 /* DeepCopyStrategy.SimpleWithRecursion */) {
                        change[1] = innerNewData = deepCopy(filteredData, true);
                    }
                    else {
                        change[1] = innerNewData = simpleDeepCopy(filteredData);
                    }
                    this.innerData[propName] = innerNewData;
                }
                ;
                this.data[propName] = filteredData;
                // reflect to attributes for properties (if needed)
                if (this._$reflectToAttributes) {
                    const be = comp.getBackendElement();
                    if (be) {
                        let attrValue = filteredData;
                        if (prop.reflectIdPrefix) {
                            const owner = comp.ownerShadowRoot;
                            if (owner) {
                                const idPrefix = owner.getHostNode()._$idPrefix;
                                if (idPrefix) {
                                    attrValue = `${idPrefix}--${filteredData}`;
                                }
                            }
                        }
                        const attrName = propName.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
                        const type = typeof attrValue;
                        if (type === 'boolean') {
                            if (filteredData)
                                be.setAttribute(attrName, '');
                            else
                                be.removeAttribute(attrName);
                        }
                        else if (type === 'object') {
                            be.setAttribute(attrName, JSON.stringify(attrValue));
                        }
                        else {
                            be.setAttribute(attrName, attrValue);
                        }
                    }
                }
                // run comparer for properties
                let comparerResult;
                if (!isSplice && (prop.comparer || this._$propertyComparer)) {
                    comparerResult = !!safeCallback('Property Comparer', prop.comparer || this._$propertyComparer, comp, [newData, oldData], comp === null || comp === void 0 ? void 0 : comp.general());
                    changed = comparerResult;
                }
                else {
                    comparerResult = oldData !== filteredData;
                }
                if (!excluded && comparerResult) {
                    propChanges.push({
                        propName,
                        prop,
                        oldValue: oldData,
                        newValue: filteredData,
                        skipModelListener,
                    });
                }
            }
            else {
                // find actual data fields for multi-level updates
                let curData = this.data;
                let curSlice = propName;
                for (let i = 1; i < path.length; i += 1) {
                    const nextSlice = path[i];
                    if (Number.isFinite(nextSlice)) {
                        if (!hasOwnProperty.call(curData, curSlice) ||
                            !Array.isArray(curData[curSlice])) {
                            ;
                            curData[curSlice] = [];
                        }
                        if (!needInnerDataUpdates && !excluded) {
                            if (nextSlice >=
                                curData[curSlice].length) {
                                combinedChanges.push([
                                    path.slice(0, i).concat('length'),
                                    true,
                                    undefined,
                                    undefined,
                                ]);
                            }
                        }
                    }
                    else {
                        if (!hasOwnProperty.call(curData, curSlice) ||
                            curData[curSlice] === null ||
                            typeof curData[curSlice] !== 'object' ||
                            Array.isArray(curData[curSlice])) {
                            ;
                            curData[curSlice] = {};
                        }
                    }
                    curData = curData[curSlice];
                    curSlice = nextSlice;
                }
                let normalizedSpliceIndex;
                if (isSplice) {
                    // splice update for arrays
                    const oldLength = curData.length;
                    const oldData = curData[curSlice];
                    if (Array.isArray(oldData)) {
                        const c = change;
                        const spliceIndex = maybeSpliceIndex;
                        normalizedSpliceIndex =
                            spliceIndex >= 0 && spliceIndex < oldData.length ? spliceIndex : oldData.length;
                        c[2] = normalizedSpliceIndex;
                        oldData.splice(normalizedSpliceIndex, maybeSpliceDel, ...newData);
                        if (!needInnerDataUpdates && !excluded && curData.length !== oldLength) {
                            combinedChanges.push([path.concat('length'), true, undefined, undefined]);
                        }
                    }
                    else {
                        triggerWarning(`An array splice change cannot be applied to a non-array value (on path "${path.join('.')}"). The change is ignored.`);
                    }
                }
                else {
                    // normal replace
                    ;
                    curData[curSlice] = newData;
                }
                // if inner data is separated, update it
                if (needInnerDataUpdates) {
                    // find actual data fields for multi-level updates again
                    curData = this.innerData;
                    curSlice = propName;
                    for (let i = 1; i < path.length; i += 1) {
                        const nextSlice = path[i];
                        if (Number.isFinite(nextSlice)) {
                            if (!hasOwnProperty.call(curData, curSlice) ||
                                !Array.isArray(curData[curSlice])) {
                                ;
                                curData[curSlice] = [];
                            }
                            if (nextSlice >=
                                curData[curSlice].length) {
                                combinedChanges.push([
                                    path.slice(0, i).concat('length'),
                                    true,
                                    undefined,
                                    undefined,
                                ]);
                            }
                        }
                        else {
                            if (!hasOwnProperty.call(curData, curSlice) ||
                                curData[curSlice] === null ||
                                typeof curData[curSlice] !== 'object' ||
                                Array.isArray(curData[curSlice])) {
                                ;
                                curData[curSlice] = {};
                            }
                        }
                        curData = curData[curSlice];
                        curSlice = nextSlice;
                    }
                    // do the inner data updates
                    let innerNewData;
                    if (dataDeepCopy === 0 /* DeepCopyStrategy.None */) {
                        change[1] = innerNewData = newData;
                    }
                    else if (normalizedSpliceIndex !== undefined) {
                        const oldLength = curData.length;
                        innerNewData = curData[curSlice];
                        let inserts;
                        if (dataDeepCopy === 2 /* DeepCopyStrategy.SimpleWithRecursion */) {
                            change[1] = inserts = deepCopy(newData, true);
                        }
                        else {
                            change[1] = inserts = simpleDeepCopy(newData);
                        }
                        ;
                        innerNewData.splice(normalizedSpliceIndex, maybeSpliceDel, ...inserts);
                        if (curData.length !== oldLength) {
                            combinedChanges.push([path.concat('length'), true, undefined, undefined]);
                        }
                    }
                    else if (dataDeepCopy === 2 /* DeepCopyStrategy.SimpleWithRecursion */) {
                        change[1] = innerNewData = deepCopy(newData, true);
                    }
                    else {
                        change[1] = innerNewData = simpleDeepCopy(newData);
                    }
                    ;
                    curData[curSlice] = innerNewData;
                }
                // triggers property updates if it is a property
                // NOTE for prop observers, oldVal will be undefined when doing a sub-path update
                if (!excluded && prop) {
                    propChanges.push({
                        propName,
                        prop,
                        oldValue: undefined,
                        newValue: newData,
                        skipModelListener: skipModelListener || false,
                    });
                }
            }
            markTriggerBitOnPath(this._$observerTree.observerRoot, this._$observerStatus, path);
            if (!excluded && changed) {
                combinedChanges.push(change);
            }
            if (this._$doingUpdates) {
                this._$doingUpdates.count += 1;
            }
        }
        // trigger data observers
        if (isChainedUpdates)
            return;
        if (this._$doingUpdates) {
            let changesCount;
            do {
                changesCount = this._$doingUpdates.count;
                triggerAndCleanTriggerBit(this._$observerTree.observers, this._$observerStatus, comp, this.data);
            } while (changesCount !== this._$doingUpdates.count);
            this._$doingUpdates = null;
        }
        // tell template engine what changed
        if (this._$recUpdateLevel > 0 && comp) {
            triggerWarning(`recursive update detected: a data update is applying during another data update for component "${comp.is}" - this may disorder node trees`, comp);
        }
        this._$recUpdateLevel += 1;
        (_a = this.updateListener) === null || _a === void 0 ? void 0 : _a.call(this, this.innerData || this.data, combinedChanges);
        this._$recUpdateLevel -= 1;
        // trigger prop observers (to simulating legacy behaviors)
        if (comp) {
            for (let i = 0; i < propChanges.length; i += 1) {
                const { propName, prop, oldValue, newValue, skipModelListener } = propChanges[i];
                if (!skipModelListener && this._$modelBindingListener) {
                    const listener = this._$modelBindingListener[propName];
                    if (listener)
                        listener(newValue);
                }
                if (prop.observer) {
                    safeCallback('Property Observer', prop.observer, comp.getMethodCaller(), [newValue, oldValue], comp);
                }
                this._$propDirty[propName] = true;
                if (comp._$mutationObserverTarget) {
                    MutationObserverTarget.callAttrObservers(comp, {
                        type: 'properties',
                        target: comp,
                        nameType: 'component-property',
                        propertyName: propName,
                    });
                }
            }
        }
    }
}
//# sourceMappingURL=data_proxy.js.map